import { z } from 'zod';
import { idempotency } from './spine';
import { customerKind } from './customer';

/**
 * Correcting what's already there.
 *
 * Nothing in the spine could be edited until this existed — no `.update()` against
 * customers, contacts, properties, jobs or the price book anywhere in the codebase. A
 * mistyped name, a customer who moved, a part whose price went up: all permanent, and the
 * only workaround (make a second customer) was permanent too.
 *
 * ── The rule that makes these schemas readable ──
 *
 * Every field is optional, and the three states are DISTINCT:
 *   absent      → leave it alone
 *   ''          → clear it (the user emptied the box on purpose)
 *   a value     → set it
 *
 * Conflating the first two is the classic patch bug: a form that sends only what changed
 * would wipe every field it didn't send, and a form that sends everything could never
 * clear anything. `''` meaning "clear" is what lets a shop delete a wrong phone number.
 *
 * ── What is deliberately NOT editable ──
 *
 * The parent of a thing. A job cannot move to a different customer, a property or contact
 * cannot move to a different customer. Visits, invoices, notes and photos already hang off
 * those rows, and re-parenting would silently re-attribute money and history that really
 * happened somewhere else. Getting the customer wrong is fixed by cancelling and re-booking,
 * which leaves a trail — not by an edit that leaves none.
 */

const editText = (max: number) => z.string().trim().max(max).optional();
const editEmail = z.string().trim().max(200).optional();

// ── Customer ─────────────────────────────────────────────────────────────────
export const customerUpdateInput = idempotency.extend({
  customerId: z.string().uuid(),
  // Residential ↔ commercial is a real correction: a shop books "John Smith" and later
  // learns it's "Smith Plumbing LLC" who pays.
  kind: customerKind.optional(),
  displayName: z.string().trim().min(1, 'Name is required').max(200).optional(),
  companyName: editText(200),
  email: editEmail,
  phone: editText(40),
  city: editText(120),
  state: z.string().trim().max(2).toUpperCase().optional(),
  leadSource: editText(80),
  /** A resale certificate or a church letter in the filing cabinet. Enforced server-side in
   *  services/tax.ts, not by the form — see the note there. */
  taxExempt: z.boolean().optional(),
});
export type CustomerUpdateInput = z.infer<typeof customerUpdateInput>;

// ── Contact ──────────────────────────────────────────────────────────────────
export const contactUpdateInput = idempotency.extend({
  contactId: z.string().uuid(),
  firstName: z.string().trim().min(1).max(100).optional(),
  lastName: editText(100),
  email: editEmail,
  phone: editText(40),
  roleLabel: editText(60),
  /** Promoting somebody demotes whoever held it — one main contact per customer. */
  isPrimary: z.boolean().optional(),
});
export type ContactUpdateInput = z.infer<typeof contactUpdateInput>;

// ── Property ─────────────────────────────────────────────────────────────────
export const propertyUpdateInput = idempotency.extend({
  propertyId: z.string().uuid(),
  label: z.string().trim().min(1).max(120).optional(),
  address1: editText(200),
  address2: editText(200),
  city: editText(120),
  state: z.string().trim().max(2).toUpperCase().optional(),
  zip: editText(12),
  /** `null` detaches the site contact; absent leaves it. */
  siteContactId: z.string().uuid().nullable().optional(),
  accessNotes: editText(2000),
  gateCode: editText(60),
});
export type PropertyUpdateInput = z.infer<typeof propertyUpdateInput>;

// ── Job ──────────────────────────────────────────────────────────────────────
export const jobUpdateInput = idempotency.extend({
  jobId: z.string().uuid(),
  title: z.string().trim().min(1).max(200).optional(),
  description: editText(4000),
  priority: z.enum(['normal', 'urgent']).optional(),
});
export type JobUpdateInput = z.infer<typeof jobUpdateInput>;

// ── Price book ───────────────────────────────────────────────────────────────
export const priceBookUpdateInput = idempotency.extend({
  itemId: z.string().uuid(),
  kind: z.enum(['service', 'labor', 'material', 'fee']).optional(),
  name: z.string().trim().min(1).max(200).optional(),
  description: editText(2000),
  unitLabel: editText(20),
  unitPriceCents: z.coerce.number().int().min(0).max(100_000_000).optional(),
  taxable: z.boolean().optional(),
  category: editText(60),
});
export type PriceBookUpdateInput = z.infer<typeof priceBookUpdateInput>;

// ── Archiving ────────────────────────────────────────────────────────────────
/**
 * Archive, never delete. A customer has invoices, a property has visits, a part is on last
 * year's jobs — the row has to stay for the history to stay true. Archiving takes it out of
 * the lists and the pickers, which is the only thing anybody actually wanted.
 */
export const archiveInput = idempotency.extend({
  id: z.string().uuid(),
  /** Bringing one back — the undo for an archive done by mistake. */
  restore: z.boolean().optional(),
});
export type ArchiveInput = z.infer<typeof archiveInput>;
